import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import BinderMoreDropdown from "@/components/BinderMoreDropdown";
import ThemeToggle from "@/components/ThemeToggle";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import NotificationBell from "@/components/NotificationBell";
import CoachChatWidget from "@/components/CoachChatWidget";
import TestimonialModal from "@/components/TestimonialModal";
import { useTranslation } from "react-i18next";
import { LogOut, LayoutDashboard, MessageSquare, Calendar, BookOpen, Users, ClipboardList, Dumbbell, Video, CreditCard, Watch, GraduationCap, CalendarDays, Hash, FileText, NotebookPen, ClipboardCheck, MessagesSquare, Sparkles, Activity, ShieldCheck, Inbox, Clock, ThumbsDown, GripVertical, MoreHorizontal, X, Gauge, Rocket, UserCog } from "lucide-react";
import PageTransition from "@/components/PageTransition";
import OnboardingTour from "@/components/OnboardingTour";
import { useEditMode } from "@/lib/editMode";
import { Sheet, SheetContent } from "@/components/ui/sheet";

// Per-section binder accent colours (navigation only — kept distinct from the
// clinician identity colours used inside calendars/appointments).
const SECTION_ACCENT = (path = "") => {
  if (path === "/app") return "#f47a20";
  if (path.startsWith("/app/plan")) return "#14B8A6";
  if (path.startsWith("/app/progress") || path.startsWith("/app/wearables")) return "#3B82F6";
  if (path.startsWith("/app/messages")) return "#A855F7";
  if (path.startsWith("/app/learn") || path.startsWith("/app/community")) return "#F59E0B";
  if (path.startsWith("/app/appointments")) return "#3B82F6";
  if (path.startsWith("/app/videos") || path.startsWith("/app/forms")) return "#A855F7";
  if (path.startsWith("/clinician/calendar") || path.startsWith("/clinician/appointments") || path.startsWith("/clinician/availability") || path.startsWith("/clinician/meetings")) return "#3B82F6";
  if (path.startsWith("/clinician/library") || path.startsWith("/clinician/templates") || path.startsWith("/clinician/physical-tests") || path.startsWith("/clinician/periodisation")) return "#22C55E";
  if (path.startsWith("/clinician/ai-library") || path.startsWith("/clinician/monitoring") || path.startsWith("/clinician/proposals") || path.startsWith("/clinician/coach-quality")) return "#A855F7";
  if (path.startsWith("/clinician/team") || path.startsWith("/clinician/community") || path.startsWith("/clinician/inquiries")) return "#14B8A6";
  if (path.startsWith("/clinician/cpd") || path.startsWith("/clinician/reflections")) return "#F59E0B";
  if (path.startsWith("/clinician/messages")) return "#A855F7";
  if (path.startsWith("/security-settings")) return "#94A3B8";
  return "#f47a20";
};

function CustomizeLayoutToggle({ testid = "customize-layout-global" }) {
  const { editing, toggle } = useEditMode();
  return (
    <button
      type="button"
      onClick={toggle}
      className="text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-md flex items-center gap-1.5 whitespace-nowrap"
      style={{
        border: "1px solid color-mix(in oklab, var(--fl-orange) 45%, transparent)",
        background: editing ? "var(--fl-orange)" : "transparent",
        color: editing ? "#0A0A0A" : "var(--fl-orange)",
      }}
      data-testid={testid}
      data-editing={editing ? "true" : "false"}
      title="Toggle Customize Layout for this tab"
    >
      <GripVertical size={12} />
      {editing ? "Done" : "Customize"}
    </button>
  );
}

// Poll interval for nav unread badges. Kept in sync with the chat auto-poll
// on /clinician/team + /messages so badges reflect fresh reality.
const UNREAD_POLL_MS = 15000;

function useUnreadBadges(role) {
  const [dmUnread, setDmUnread] = useState(0);
  const [teamUnread, setTeamUnread] = useState(0);
  const [coachUnread, setCoachUnread] = useState(0);
  const [proposalsUnread, setProposalsUnread] = useState(0);
  const [reviewsOpen, setReviewsOpen] = useState(0);

  useEffect(() => {
    if (!role) return undefined;
    let alive = true;

    const load = async () => {
      const jobs = [];
      jobs.push(
        api.get("/messages/unread-summary").then((r) => alive && setDmUnread(r.data?.total || 0)).catch(() => {})
      );
      jobs.push(
        api.get("/notifications/counts").then((r) => {
          if (!alive) return;
          setCoachUnread(r.data?.ai_coach || 0);
          setProposalsUnread(r.data?.proposals || 0);
        }).catch(() => {})
      );
      if (role === "clinician" || role === "admin") {
        jobs.push(
          api.get("/team/unread").then((r) => alive && setTeamUnread(r.data?.total || 0)).catch(() => {})
        );
        jobs.push(
          api.get("/programme-generator/reviews", { params: { status: "pending" } })
            .then((r) => alive && setReviewsOpen(r.data?.counts?.pending || 0)).catch(() => {})
        );
      }
      await Promise.all(jobs);
    };

    load();
    const id = setInterval(load, UNREAD_POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, [role]);

  return { dmUnread, teamUnread, coachUnread, proposalsUnread, reviewsOpen };
}

// ── Compact utility-rail profile popover (desktop) ──────────────────────────
// Rendered via a portal with fixed positioning so the menu escapes the rail's
// stacking context / overflow and always sits above the binder navigation.
function ProfilePopover({ user, onLogout, t }) {
  const [open, setOpen] = useState(false);
  const [bouncing, setBouncing] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ left: 0, bottom: 0 });

  const place = () => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const MENU_W = 240, GAP = 0, MARGIN = 8;
    // Prefer to sit just right of the avatar; flip to its left if that overflows
    // (keeps the menu visually attached and fully inside the viewport on iPad).
    let left = r.right + GAP;
    if (left + MENU_W > window.innerWidth - MARGIN) left = r.left - GAP - MENU_W;
    left = Math.min(Math.max(MARGIN, left), window.innerWidth - MENU_W - MARGIN);
    const bottom = Math.min(
      Math.max(MARGIN, Math.round(window.innerHeight - r.bottom)),
      Math.max(MARGIN, window.innerHeight - 120),
    );
    setPos({ left, bottom });
  };

  useLayoutEffect(() => { if (open) place(); }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const reposition = () => place();
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onDown = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open]);

  const initial = (user?.name || "?").trim().charAt(0).toUpperCase();
  const roleColor = user?.role === "patient" ? "var(--fl-orange)"
    : user?.role === "clinician" ? "#3B82F6" : "#94A3B8";
  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => { setOpen((v) => !v); setBouncing(true); setTimeout(() => setBouncing(false), 420); }}
        data-testid="rail-profile-btn"
        aria-expanded={open}
        aria-haspopup="menu"
        className={`w-10 h-10 rounded-full grid place-items-center text-sm font-bold transition-transform hover:scale-105${bouncing ? " fl-avatar-bounce" : ""}`}
        style={{ background: `color-mix(in oklab, ${roleColor} 20%, transparent)`, color: roleColor, border: `1px solid color-mix(in oklab, ${roleColor} 45%, transparent)` }}
        title={user?.name}
      >
        {initial}
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          role="menu"
          className="fixed w-60 fl-card p-3 z-[200]"
          style={{ left: pos.left, bottom: pos.bottom, maxHeight: `calc(100dvh - ${pos.bottom + 8}px)`, overflowY: "auto", boxShadow: "var(--fl-shadow)", border: `1px solid color-mix(in oklab, ${roleColor} 45%, transparent)` }}
          data-testid="rail-profile-menu"
        >
          <div className="px-1 pb-3 mb-2" style={{ borderBottom: "1px solid var(--fl-border)" }}>
            <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--fl-muted-2)" }}>Signed in as</div>
            <div className="text-sm font-semibold truncate">{user?.name}</div>
            <div className="fl-label mt-0.5 text-[10px]">{user?.role}{user?.is_admin ? " · admin" : ""}</div>
          </div>
          {user?.is_admin && (
            <button
              type="button"
              data-testid="nav-admin-dashboard"
              onClick={() => { window.open("/admin", "_blank", "noopener"); setOpen(false); }}
              className="w-full flex items-center gap-2 text-xs px-2 py-2 rounded-lg mb-1 transition-colors"
              style={{ border: "1px solid color-mix(in oklab, var(--fl-orange) 45%, transparent)", color: "var(--fl-orange)" }}
            >
              <ShieldCheck size={14} /> Admin Dashboard
            </button>
          )}
          {user?.role === "patient" && (
            <Link
              to="/app/profile"
              onClick={() => setOpen(false)}
              data-testid="rail-profile-link"
              className="w-full flex items-center gap-2 text-xs px-2 py-2 rounded-lg hover:bg-white/5 transition-colors"
              style={{ color: "var(--fl-text)" }}
            >
              <UserCog size={14} /> Account &amp; Profile
            </Link>
          )}
          <Link
            to="/security-settings"
            onClick={() => setOpen(false)}
            data-testid="rail-security-link"
            className="w-full flex items-center gap-2 text-xs px-2 py-2 rounded-lg hover:bg-white/5 transition-colors"
            style={{ color: "var(--fl-text)" }}
          >
            <ShieldCheck size={14} /> {t("nav.security", "Security")}
          </Link>
          <div className="flex items-center justify-between px-2 py-2">
            <span className="text-xs" style={{ color: "var(--fl-muted-2)" }}>{t("common.language")}</span>
            <LanguageSwitcher align="right" />
          </div>
          <div className="flex items-center justify-between px-2 py-2">
            <span className="text-xs" style={{ color: "var(--fl-muted-2)" }}>{t("common.theme")}</span>
            <ThemeToggle />
          </div>
          <button
            data-testid="rail-restart-tour"
            onClick={() => { setOpen(false); window.dispatchEvent(new CustomEvent("fl:restart-onboarding")); }}
            className="w-full flex items-center gap-2 text-xs px-2 py-2 rounded-lg hover:bg-white/5 transition-colors"
            style={{ color: "var(--fl-text)" }}
          >
            <Rocket size={14} /> {t("nav.restartTour", "Restart tour")}
          </button>
          <button
            data-testid="btn-logout"
            onClick={async () => { setOpen(false); await onLogout(); }}
            className="w-full flex items-center gap-2 text-xs px-2 py-2 rounded-lg mt-1 hover:bg-white/5 transition-colors"
            style={{ color: "var(--fl-muted)" }}
          >
            <LogOut size={14} /> {t("common.signOut")}
          </button>
          <Link to="/privacy" onClick={() => setOpen(false)} className="block text-[10px] px-2 pt-2" style={{ color: "var(--fl-muted-2)" }} data-testid="dash-privacy-link">
            Privacy Policy
          </Link>
        </div>,
        document.body
      )}
    </>
  );
}

export default function DashLayout({ children }) {
  const { user, logout } = useAuth();
  const { setRole } = useTheme();
  const { t } = useTranslation();
  const loc = useLocation();
  const nav = useNavigate();
  const { dmUnread, teamUnread, coachUnread, proposalsUnread, reviewsOpen } = useUnreadBadges(user?.role);
  const [moreOpen, setMoreOpen] = useState(false);
  const isClin = user?.role === "clinician" || user?.role === "admin";
  // Admin dashboard access mirrors the backend/route guard: role==="admin" OR is_admin.
  const isAdmin = user?.is_admin || user?.role === "admin";
  const brandHref = user?.role === "clinician" ? "/clinician"
                  : user?.role === "admin" ? "/admin"
                  : "/app";

  useEffect(() => {
    if (user?.role) setRole(user.role);
    return () => setRole(null);
  }, [user?.role, setRole]);

  // Mobile bottom-nav auto-centres the active tab once per route change.
  const mobileNavRef = useRef(null);
  useEffect(() => {
    const el = mobileNavRef.current;
    if (!el) return;
    const active = el.querySelector('[data-active="true"]');
    if (!active) return;
    const target = active.offsetLeft - (el.clientWidth - active.offsetWidth) / 2;
    el.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
  }, [loc.pathname]);

  // Feature-specific nav gating (entitlements).
  const _ent = user?.entitlements || {};
  const CAP_FOR = {
    "/app/plan": "can_access_my_plan",
    "/app/coach": "can_check_in",
    "/app/forms": "can_access_forms",
    "/app/progress": "can_access_metrics",
    "/app/wearables": "can_access_metrics",
  };
  const allowNav = (item) => {
    const capKey = Object.keys(CAP_FOR).find((p) => item.to.startsWith(p));
    return !capKey || _ent[CAP_FOR[capKey]] !== false;
  };

  // ── PATIENT navigation model ────────────────────────────────────────────
  // Primary binder tabs = the 5 high-frequency destinations. Everything else
  // lives in the "More" overflow dropdown (desktop) / flat scroller (mobile).
  const patientPrimary = [
    { to: "/app",          label: t("nav.today"),               icon: LayoutDashboard, testid: "nav-today" },
    { to: "/app/plan",     label: t("nav.plan"),                icon: Dumbbell,        testid: "nav-plan" },
    { to: "/app/coach",    label: t("nav.checkin", "Check-In"), icon: Sparkles,        testid: "nav-coach", badge: coachUnread },
    { to: "/app/progress", label: t("nav.progress"),            icon: Activity,        testid: "nav-progress" },
    { to: "/app/messages", label: t("nav.messages"),            icon: MessageSquare,   testid: "nav-messages", badge: dmUnread },
  ].filter(allowNav);
  const patientMore = [
    { to: "/app/appointments", label: t("nav.appointments"),        icon: Calendar,       testid: "nav-appointments" },
    { to: "/app/videos",       label: t("nav.videos"),              icon: Video,          testid: "nav-videos" },
    { to: "/app/forms",        label: t("nav.forms"),               icon: FileText,       testid: "nav-forms" },
    { to: "/app/learn",        label: t("nav.learn"),               icon: BookOpen,       testid: "nav-learn" },
    { to: "/app/community",    label: t("nav.community"),           icon: MessagesSquare, testid: "nav-community" },
    { to: "/app/packages",     label: t("nav.membership"),          icon: CreditCard,     testid: "nav-packages" },
    { to: "/app/wearables",    label: t("nav.wearables"),           icon: Watch,          testid: "nav-wearables" },
    { to: "/app/profile",      label: "Account & Profile",          icon: UserCog,        testid: "nav-profile" },
    { to: "/security-settings",label: t("nav.security", "Security"),icon: ShieldCheck,    testid: "nav-security-more" },
  ].filter(allowNav);
  // Progress sub-tabs (shown on the secondary binder row while on /app/progress).
  const patientProgressSub = [
    { to: "/app/progress",                  label: t("nav.progress"),                            testid: "nav-progress-sub" },
    { to: "/app/progress?section=wellness", label: t("nav.physicalWellness", "Physical Wellness"), testid: "nav-physical-wellness" },
    { to: "/app/progress?section=tests",    label: t("nav.physicalTests", "Physical Tests"),      testid: "nav-physical-tests" },
  ];
  // Flat list (mobile patient scroller) — primary + overflow combined.
  const patientLinks = [...patientPrimary, ...patientMore];

  // ── CLINICIAN / ADMIN navigation model (grouped) ────────────────────────
  const clinGroups = [
    { key: "patients", label: "Patients", icon: Users, testid: "nav-group-patients", items: [
        { to: "/clinician", label: t("nav.caseload"), icon: Users, testid: "nav-caseload" },
        { to: "/clinician/messages", label: t("nav.messages"), icon: MessageSquare, testid: "nav-messages", badge: dmUnread },
        { to: "/clinician/forms", label: t("nav.forms"), icon: FileText, testid: "nav-c-forms" },
        { to: "/clinician/notes", label: "Assessments & Notes", icon: NotebookPen, testid: "nav-clinical-notes" },
        { to: "/clinician/personalisation-reviews", label: "Personalisation Reviews", icon: ClipboardCheck, testid: "nav-personalisation-reviews", badge: reviewsOpen },
    ]},
    { key: "schedule", label: t("nav.schedule", "Schedule"), icon: CalendarDays, testid: "nav-group-schedule", items: [
        { to: "/clinician/calendar", label: t("nav.calendar"), icon: CalendarDays, testid: "nav-calendar" },
        { to: "/clinician/appointments", label: t("nav.schedule"), icon: Calendar, testid: "nav-schedule" },
        { to: "/clinician/availability", label: "Availability", icon: Clock, testid: "nav-availability" },
        { to: "/clinician/meetings", label: t("nav.meetings"), icon: Video, testid: "nav-meetings" },
    ]},
    { key: "programmes", label: "Programmes", icon: Dumbbell, testid: "nav-group-programmes", items: [
        { to: "/clinician/library", label: t("nav.exercises"), icon: ClipboardList, testid: "nav-library" },
        { to: "/clinician/templates", label: "Programme templates", icon: Dumbbell, testid: "nav-templates" },
        { to: "/clinician/periodisation", label: "Periodisation", icon: CalendarDays, testid: "nav-periodisation" },
        { to: "/clinician/physical-tests", label: "Physical Tests", icon: Gauge, testid: "nav-physical-tests-lib" },
    ]},
    { key: "ai", label: "AI & Quality", icon: Sparkles, testid: "nav-group-ai", items: [
        { to: "/clinician/ai-library", label: "AI Library", icon: BookOpen, testid: "nav-ai-library" },
        { to: "/clinician/monitoring", label: t("nav.monitoring"), icon: Activity, testid: "nav-monitoring" },
        { to: "/clinician/proposals", label: "AI Proposals", icon: Sparkles, testid: "nav-proposals", badge: proposalsUnread },
        { to: "/clinician/coach-quality", label: "Coach Quality", icon: ThumbsDown, testid: "nav-coach-quality" },
    ]},
    { key: "practice", label: "Practice", icon: Hash, testid: "nav-group-practice", items: [
        { to: "/clinician/team", label: t("nav.teamChannels"), icon: Hash, testid: "nav-team", badge: teamUnread },
        { to: "/clinician/community", label: t("nav.community"), icon: MessagesSquare, testid: "nav-community-clin" },
        { to: "/clinician/inquiries", label: t("nav.inquiries", "Inquiries"), icon: Inbox, testid: "nav-inquiries" },
    ]},
    { key: "professional", label: "Professional", icon: GraduationCap, testid: "nav-group-professional", items: [
        { to: "/clinician/cpd", label: t("nav.cpd"), icon: GraduationCap, testid: "nav-cpd" },
        { to: "/clinician/reflections", label: t("nav.reflections"), icon: NotebookPen, testid: "nav-reflections" },
    ]},
    { key: "account", label: "Account", icon: ShieldCheck, testid: "nav-group-account", items: [
        { to: "/security-settings", label: "Security", icon: ShieldCheck, testid: "nav-security" },
    ]},
  ];
  const CLIN_PRIMARY_KEYS = ["patients", "schedule", "programmes", "ai", "practice"];
  const clinPrimaryGroups = clinGroups.filter((g) => CLIN_PRIMARY_KEYS.includes(g.key));
  const clinMoreItems = clinGroups.filter((g) => !CLIN_PRIMARY_KEYS.includes(g.key)).flatMap((g) => g.items);
  const groupAccent = (g) => SECTION_ACCENT((g.items[0]?.to || "").split("?")[0]);
  const activeClinGroup = clinGroups.find((g) => g.items.some((it) => it.to.split("?")[0] === loc.pathname))
    || (loc.pathname.startsWith("/clinician/patient") ? clinGroups.find((g) => g.key === "patients") : undefined);
  const clinSubItems = activeClinGroup ? activeClinGroup.items : [];

  // ── Mobile clinician navigation (unchanged: 4 fixed tabs + More sheet) ──
  const CLIN_FIXED_PATHS = ["/clinician", "/clinician/calendar", "/clinician/messages", "/clinician/appointments"];
  const clinFixedTabs = [
    { to: "/clinician",              label: t("nav.caseload"), icon: Users,         testid: "nav-caseload" },
    { to: "/clinician/calendar",     label: t("nav.calendar"), icon: CalendarDays,  testid: "nav-calendar" },
    { to: "/clinician/messages",     label: t("nav.messages"), icon: MessageSquare, testid: "nav-messages", badge: dmUnread },
    { to: "/clinician/appointments", label: t("nav.schedule"), icon: Calendar,      testid: "nav-schedule" },
  ];
  const clinMoreBadge = Number(teamUnread || 0) + Number(proposalsUnread || 0) + Number(coachUnread || 0);
  const moreTabActive = isClin && !CLIN_FIXED_PATHS.includes(loc.pathname);
  const clinMoreGroups = clinGroups
    .map((g) => ({ ...g, items: g.items.filter((it) => !CLIN_FIXED_PATHS.includes(it.to)) }))
    .filter((g) => g.items.length);

  // Mobile flat list for the patient scroller.
  const mobileLinks = user?.role === "patient" ? patientLinks : clinFixedTabs;

  // Active-state helper (progress ?section= variants distinguished).
  const tabActive = (to) => {
    const [p, q] = to.split("?");
    if (p === "/app/progress") {
      if (loc.pathname !== "/app/progress") return false;
      const cur = new URLSearchParams(loc.search).get("section");
      const wanted = q ? new URLSearchParams(q).get("section") : null;
      const norm = (v) => (v === "wellness" || v === "tests" ? v : "progress");
      return norm(cur) === norm(wanted);
    }
    return loc.pathname === p;
  };

  // Primary binder tabs highlight on pathname only (any ?section= variant of
  // Progress keeps the primary Progress divider connected). Section-specific
  // matching stays scoped to the sub-tabs via tabActive().
  const primaryActive = (to) => loc.pathname === to.split("?")[0];

  const showCustomize = user?.role === "patient" ? loc.pathname === "/app" : true;

  // Vertical binder folder-divider renderer.
  const renderVTab = ({ to, label, testid, icon: Icon, accent, active, badge = 0 }) => (
    <Link
      key={to}
      to={to}
      data-testid={testid}
      data-active={active ? "true" : "false"}
      aria-current={active ? "page" : undefined}
      className="fl-vtab"
      style={{ "--fl-section": accent }}
    >
      {Icon && <Icon size={16} className="shrink-0" />}
      <span className="truncate">{label}</span>
      {Number(badge) > 0 && (
        <span data-testid={`${testid}-badge`} className="fl-binder-badge ml-auto">{badge > 99 ? "99+" : badge}</span>
      )}
    </Link>
  );

  return (
    <div className="min-h-screen flex fl-noise" style={{ background: "var(--fl-bg)" }}>
      {/* ── Desktop vertical binder navigation (236px) ─────────────────── */}
      <aside
        className="hidden md:flex flex-col w-[236px] shrink-0 sticky fl-vbinder"
        style={{ top: "var(--fl-banner-height, 0px)", height: "calc(100vh - var(--fl-banner-height, 0px))" }}
        data-testid="vbinder"
      >
        <Link to={brandHref} className="block px-4 pt-5 pb-4" data-testid="brand-home" title="Home">
          <Logo variant="full" size="md" />
        </Link>
        <nav
          className="flex-1 flex flex-col gap-1.5 pl-3 pr-0 pb-3"
          data-testid="vbinder-nav"
          onKeyDown={(e) => {
            if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
            const items = Array.from(e.currentTarget.querySelectorAll('a[href], button:not([disabled])'));
            const i = items.indexOf(document.activeElement);
            if (i === -1) return;
            e.preventDefault();
            const n = e.key === "ArrowDown" ? (i + 1) % items.length : (i - 1 + items.length) % items.length;
            items[n]?.focus();
          }}
        >
          {user?.role === "patient" ? (
            <>
              {patientPrimary.map((l) => renderVTab({ to: l.to, label: l.label, testid: l.testid, icon: l.icon, accent: SECTION_ACCENT(l.to.split("?")[0]), active: primaryActive(l.to), badge: l.badge }))}
              <BinderMoreDropdown vertical items={patientMore} activePath={loc.pathname} accent="#f47a20" accentFor={(l) => SECTION_ACCENT(l.to.split("?")[0])} testidPrefix="nav" />
            </>
          ) : (
            <>
              {clinPrimaryGroups.map((g) => {
                const active = activeClinGroup?.key === g.key;
                const badge = g.items.reduce((s, it) => s + Number(it.badge || 0), 0);
                return renderVTab({ to: g.items[0].to, label: g.label, testid: g.testid, icon: g.icon, accent: groupAccent(g), active, badge });
              })}
              <BinderMoreDropdown vertical items={clinMoreItems} activePath={loc.pathname} accent="#94A3B8" accentFor={(l) => SECTION_ACCENT(l.to.split("?")[0])} testidPrefix="nav" />
            </>
          )}
        </nav>
        <div className="flex items-center gap-1.5 px-3 py-3" style={{ borderTop: "1px solid var(--fl-border)" }} data-testid="vbinder-footer">
          <NotificationBell />
          {isAdmin && (
            <button
              type="button"
              onClick={() => nav("/admin")}
              data-testid="rail-admin-switch"
              className="w-9 h-9 rounded-lg grid place-items-center transition-colors hover:brightness-110"
              style={{ border: "1px solid color-mix(in oklab, #94A3B8 50%, transparent)", color: "#94A3B8" }}
              title="Switch to Admin dashboard"
            >
              <ShieldCheck size={16} />
            </button>
          )}
          <div className="flex-1" />
          <ProfilePopover user={user} onLogout={async () => { await logout(); nav("/"); }} t={t} />
        </div>
      </aside>

      {/* ── Right column: header + binder + content ─────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Desktop header: compact secondary sub-tab row + customize toggle.
            Primary section nav now lives in the left vertical binder; the tab
            name itself identifies the section, so no repeated heading here. */}
        <header
          className="hidden md:block sticky z-40"
          style={{ top: "var(--fl-banner-height, 0px)", background: "var(--fl-bg)" }}
          data-testid="binder-header"
        >
          {(() => {
            let sub = [];
            if (user?.role === "patient") {
              if (loc.pathname === "/app/progress") sub = patientProgressSub;
            } else if (clinSubItems.length > 1) {
              sub = clinSubItems;
            }
            const hasSub = sub.length > 0;
            if (!hasSub && !showCustomize) return null;
            const accent = SECTION_ACCENT(loc.pathname);
            return (
              <div className="flex items-center justify-between gap-3 px-6 lg:px-8 pt-4 pb-1">
                <div className="min-w-0 flex-1 flex items-center gap-1.5 overflow-x-auto no-scrollbar" data-testid="binder-subtabs">
                  {sub.map((s) => {
                    const active = tabActive(s.to);
                    return (
                      <Link
                        key={s.to + s.testid}
                        to={s.to}
                        data-testid={s.testid}
                        data-active={active ? "true" : "false"}
                        className="fl-subtab shrink-0"
                        style={{ "--fl-section": accent }}
                      >
                        {s.label}
                        {Number(s.badge || 0) > 0 && (
                          <span className="ml-1.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold text-white" style={{ background: "var(--fl-orange)" }}>{s.badge > 99 ? "99+" : s.badge}</span>
                        )}
                      </Link>
                    );
                  })}
                </div>
                {showCustomize && <CustomizeLayoutToggle />}
              </div>
            );
          })()}
        </header>

        {/* Mobile top bar */}
        <div
          className="md:hidden fixed left-0 right-0 z-50 backdrop-blur-xl px-4 py-3 flex items-center justify-between"
          style={{ top: "var(--fl-banner-height, 0px)", background: "color-mix(in oklab, var(--fl-bg) 70%, transparent)", borderBottom: "1px solid var(--fl-border)" }}
        >
          <div className="flex items-center gap-2 min-w-0">
            <Link to={brandHref} className="inline-flex">
              <Logo variant="wordmark" size="sm" />
            </Link>
            {user?.role && (
              <span
                data-testid="mobile-role-pill"
                className="text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
                style={
                  user.role === "patient"
                    ? { background: "color-mix(in oklab, var(--fl-orange) 18%, transparent)", color: "var(--fl-orange)", border: "1px solid color-mix(in oklab, var(--fl-orange) 45%, transparent)" }
                    : user.role === "clinician"
                    ? { background: "rgba(59,130,246,.16)", color: "#60A5FA", border: "1px solid rgba(59,130,246,.45)" }
                    : { background: "rgba(148,163,184,.14)", color: "#94A3B8", border: "1px solid rgba(148,163,184,.35)" }
                }
              >
                {user.role === "patient" ? "Patient" : user.role === "clinician" ? "Clinician" : "Admin"}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isClin ? (
              <>
                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => nav("/admin")}
                    data-testid="rail-admin-switch-mobile"
                    className="text-[11px] px-2 py-1.5 rounded-lg flex items-center gap-1 whitespace-nowrap"
                    style={{ border: "1px solid color-mix(in oklab, #94A3B8 50%, transparent)", color: "#94A3B8" }}
                    title="Switch to Admin dashboard"
                  >
                    <ShieldCheck size={13} /> Admin
                  </button>
                )}
                <NotificationBell testidSuffix="mobile" />
              </>
            ) : (
              <>
                {loc.pathname === "/app" && <CustomizeLayoutToggle testid="customize-layout-global-mobile" />}
                <NotificationBell testidSuffix="mobile" />
                <ThemeToggle />
                <button onClick={async () => { await logout(); nav("/"); }} style={{ color: "var(--fl-muted)" }} data-testid="btn-logout-mobile"><LogOut size={16} /></button>
              </>
            )}
          </div>
        </div>

        <main className="fl-section-surface flex-1 pt-16 md:pt-0 pb-24 md:pb-0 min-w-0 overflow-x-hidden" style={{ "--fl-section": SECTION_ACCENT(loc.pathname) }}>
          <PageTransition className="px-4 md:px-8 lg:px-10 py-5 md:py-7 max-w-6xl fl-page">{children}</PageTransition>
          <div
            className="px-6 md:px-10 pb-6 md:pb-4 text-[11px] leading-snug"
            style={{ color: "var(--fl-muted-2)" }}
            data-testid="emergency-notice-dash"
          >
            <b className="text-[color:var(--fl-orange)]">Not an emergency service.</b>{" "}
            If you or someone else is experiencing a medical emergency, call your local emergency services immediately
            (999 in the UK, 112 in the EU, 911 in the US).
          </div>
        </main>
      </div>

      {/* Mobile bottom nav (patient) — horizontally scrollable */}
      {!isClin && (
        <nav
          className="md:hidden fixed bottom-0 left-0 right-0 z-50 backdrop-blur-xl"
          style={{ background: "color-mix(in oklab, var(--fl-bg) 88%, transparent)", borderTop: "1px solid var(--fl-border)" }}
        >
          <div className="relative">
            <div aria-hidden="true" className="pointer-events-none absolute top-0 left-0 h-full w-8 z-10" style={{ background: "linear-gradient(to right, var(--fl-bg) 15%, transparent)" }} />
            <div aria-hidden="true" className="pointer-events-none absolute top-0 right-0 h-full w-8 z-10" style={{ background: "linear-gradient(to left, var(--fl-bg) 15%, transparent)" }} />
            <div
              ref={mobileNavRef}
              className="flex overflow-x-auto no-scrollbar snap-x snap-mandatory py-2 px-3 gap-1"
              style={{ WebkitOverflowScrolling: "touch", scrollBehavior: "smooth" }}
              data-testid="mobile-nav-scroll"
            >
              {mobileLinks.map((l) => {
                const Icon = l.icon;
                const active = primaryActive(l.to);
                const badge = Number(l.badge || 0);
                return (
                  <Link
                    key={l.to}
                    to={l.to}
                    data-testid={`m-${l.testid}`}
                    data-active={active ? "true" : "false"}
                    className="snap-center flex-shrink-0 flex flex-col items-center justify-center gap-1 min-w-[64px] px-3 py-1.5 text-[10px] rounded-md transition-all duration-200 relative"
                    style={{ color: active ? "var(--fl-orange)" : "var(--fl-muted-2)", background: active ? "color-mix(in oklab, var(--fl-orange) 12%, transparent)" : "transparent" }}
                  >
                    {badge > 0 && (
                      <span className="absolute top-0.5 right-2 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold text-white" style={{ background: "var(--fl-orange)" }}>{badge > 99 ? "99+" : badge}</span>
                    )}
                    <Icon size={18} /> {l.label}
                  </Link>
                );
              })}
            </div>
          </div>
        </nav>
      )}

      {/* Mobile bottom nav (clinician/admin) — 4 fixed tabs + More */}
      {isClin && (
        <nav
          className="md:hidden fixed bottom-0 left-0 right-0 z-50 backdrop-blur-xl"
          style={{ background: "color-mix(in oklab, var(--fl-bg) 92%, transparent)", borderTop: "1px solid var(--fl-border)", paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
          data-testid="clin-mobile-nav"
        >
          <div className="grid grid-cols-5 px-1 py-1.5">
            {clinFixedTabs.map((l) => {
              const Icon = l.icon;
              const active = loc.pathname === l.to;
              const badge = Number(l.badge || 0);
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`m-${l.testid}`}
                  data-active={active ? "true" : "false"}
                  className="relative flex flex-col items-center justify-center gap-1 py-1.5 text-[10px] rounded-lg transition-colors"
                  style={{ color: active ? "var(--fl-orange)" : "var(--fl-muted-2)", background: active ? "color-mix(in oklab, var(--fl-orange) 12%, transparent)" : "transparent" }}
                >
                  {badge > 0 && (
                    <span className="absolute top-0 right-3 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold text-white" style={{ background: "var(--fl-orange)" }}>{badge > 99 ? "99+" : badge}</span>
                  )}
                  <Icon size={20} />
                  <span className="truncate max-w-[64px]">{l.label}</span>
                </Link>
              );
            })}
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              data-testid="m-nav-more"
              data-active={moreTabActive ? "true" : "false"}
              className="relative flex flex-col items-center justify-center gap-1 py-1.5 text-[10px] rounded-lg transition-colors"
              style={{ color: moreTabActive ? "var(--fl-orange)" : "var(--fl-muted-2)", background: moreTabActive ? "color-mix(in oklab, var(--fl-orange) 12%, transparent)" : "transparent" }}
            >
              {clinMoreBadge > 0 && (
                <span className="absolute top-0 right-3 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold text-white" style={{ background: "var(--fl-orange)" }}>{clinMoreBadge > 99 ? "99+" : clinMoreBadge}</span>
              )}
              <MoreHorizontal size={20} />
              <span>More</span>
            </button>
          </div>
        </nav>
      )}

      {/* Mobile "More" bottom sheet (clinician/admin) */}
      {isClin && (
        <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
          <SheetContent
            side="bottom"
            className="md:hidden rounded-t-2xl border-0 p-0 max-h-[85vh] overflow-y-auto [&>button]:hidden"
            style={{ background: "var(--fl-bg)", borderTop: "1px solid var(--fl-border)" }}
            data-testid="clin-more-sheet"
          >
            <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-4 backdrop-blur-xl" style={{ background: "color-mix(in oklab, var(--fl-bg) 85%, transparent)", borderBottom: "1px solid var(--fl-border)" }}>
              <div className="text-sm font-semibold" style={{ color: "var(--fl-text)" }}>More</div>
              <button type="button" onClick={() => setMoreOpen(false)} data-testid="more-sheet-close" style={{ color: "var(--fl-muted)" }}>
                <X size={18} />
              </button>
            </div>

            <div className="px-4 py-4 space-y-4">
              {clinMoreGroups.map((g) => (
                <div key={g.key}>
                  <div className="fl-label text-[10px] px-1 mb-2 flex items-center gap-1.5" style={{ color: "var(--fl-muted-2)" }}>
                    <g.icon size={12} /> {g.label}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {g.items.map((l) => {
                      const Icon = l.icon;
                      const active = loc.pathname === l.to;
                      const badge = Number(l.badge || 0);
                      return (
                        <Link
                          key={l.to}
                          to={l.to}
                          onClick={() => setMoreOpen(false)}
                          data-testid={`more-${l.testid}`}
                          className="relative flex flex-col items-center justify-center gap-1.5 py-3 px-1 rounded-xl text-[11px] text-center min-h-[72px] transition-colors"
                          style={{ color: active ? "var(--fl-orange)" : "var(--fl-text)", background: active ? "color-mix(in oklab, var(--fl-orange) 12%, transparent)" : "color-mix(in oklab, var(--fl-text) 5%, transparent)", border: "1px solid var(--fl-border)" }}
                        >
                          {badge > 0 && (
                            <span className="absolute top-1.5 right-2 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold text-white" style={{ background: "var(--fl-orange)" }}>{badge > 99 ? "99+" : badge}</span>
                          )}
                          <Icon size={20} className={active ? "text-[color:var(--fl-orange)]" : ""} />
                          <span className="leading-tight">{l.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="px-5 py-4 space-y-3" style={{ borderTop: "1px solid var(--fl-border)" }}>
              {user?.is_admin && (
                <button
                  type="button"
                  data-testid="more-admin-dashboard"
                  onClick={() => { window.open("/admin", "_blank", "noopener"); setMoreOpen(false); }}
                  className="flex items-center justify-center gap-2 text-sm px-3 py-2.5 rounded-xl w-full font-medium"
                  style={{ border: "1px solid color-mix(in oklab, var(--fl-orange) 45%, transparent)", color: "var(--fl-orange)" }}
                >
                  <ShieldCheck size={16} /> Admin Dashboard
                </button>
              )}
              <div className="flex items-center justify-between">
                <div className="text-xs" style={{ color: "var(--fl-muted-2)" }}>Customize</div>
                <CustomizeLayoutToggle testid="customize-layout-more" />
              </div>
              <div className="flex items-center justify-between">
                <div className="text-xs" style={{ color: "var(--fl-muted-2)" }}>{t("common.language")}</div>
                <LanguageSwitcher align="left" />
              </div>
              <div className="flex items-center justify-between">
                <div className="text-xs" style={{ color: "var(--fl-muted-2)" }}>{t("common.theme")}</div>
                <ThemeToggle />
              </div>
              <div className="pt-1">
                <div className="text-xs" style={{ color: "var(--fl-muted-2)" }}>Signed in as</div>
                <div className="text-sm font-medium">{user?.name}</div>
                <div className="fl-label mt-1">{user?.role}{user?.is_admin ? " · admin" : ""}</div>
              </div>
              <button
                data-testid="more-logout"
                onClick={async () => { setMoreOpen(false); await logout(); nav("/"); }}
                className="flex items-center gap-2 text-sm px-3 py-2.5 rounded-xl w-full"
                style={{ border: "1px solid var(--fl-border)", color: "var(--fl-muted)" }}
              >
                <LogOut size={16} /> {t("common.signOut")}
              </button>
              <Link to="/privacy" onClick={() => setMoreOpen(false)} className="block text-[10px] pt-1" style={{ color: "var(--fl-muted-2)" }} data-testid="more-privacy-link">
                Privacy Policy
              </Link>
            </div>
          </SheetContent>
        </Sheet>
      )}

      <CoachChatWidget />
      <OnboardingTour />
      <TestimonialModal />
    </div>
  );
}
